import i18n from 'i18next'

import languageEncoding from 'detect-file-encoding-and-language'
import PostalMime from 'postal-mime'

import { creactReactObjMgr } from 'utils/ui'
import { EMAIL_STATE } from './defConstants'
import type { EmailData } from './defConstants'
import { parseEml as parseEmlFormat } from 'libs/eml-format'

export async function parseEMLByPostalMime(buffer: any): Promise<any> {
  let postalMimeEML = null

  // eml 파일이 잘못 생성된 경우, 중복 표시되는 문제를 방지하기 위해 추가한 부분.
  // @todo - 충분히 시간이 지나면 제거할 것.(1년?)
  const decoder = new TextDecoder('utf-8')
  let decodedString = decoder.decode(buffer)
  const emlData: any = parseEmlFormat(decodedString)

  let hasCharset = false
  let textPart = null
  let htmlPart = null
  let mixedPart = null

  const parseContentType = (contentType: string) => {
    const ret = {}
    const items = contentType.split(';')
    items.forEach((item) => {
      const txt = item.trim()
      const pos = txt.indexOf('=')
      if (pos > 0) {
        const key = txt.substring(0, pos).trim()
        let v = txt.substring(pos + 1).trim()
        if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
          v = v.slice(1, -1)
        }
        ret[key.trim()] = v
      }
    })
    return ret
  }

  const checkPart = (part: any) => {
    Object.entries(part.headers).forEach(([key, value]) => {
      if (value != null && key.toLowerCase() === 'content-type') {
        const value0 = Array.isArray(value) ? value[value.length - 1] : value
        const lowerValue = (value0 as string).toLowerCase()
        if (lowerValue.includes('charset')) {
          hasCharset = true
        }
        if (lowerValue.includes('text/plain')) {
          textPart = part
        } else if (lowerValue.includes('text/html')) {
          htmlPart = part
        } else if (lowerValue.includes('multipart/mixed')) {
          mixedPart = part
          mixedPart.typeInfo = parseContentType(value as string)
        }
      }
    })
    if (Array.isArray(part.body)) {
      part.body.some((item: any) => {
        // const { part, boundary } = item
        checkPart(item.part)
      })
    }
  }

  checkPart(emlData)

  if (hasCharset === false) {
    try {
      const file = new File([buffer], 'temp.eml', { type: 'message/rfc822' })
      const encodingInfo = await languageEncoding(file)
      // console.log(' --- encoding : ', encodingInfo)
      if (encodingInfo != null && encodingInfo.encoding != null) {
        const bytes = new Uint8Array(buffer)
        decodedString = new TextDecoder(encodingInfo.encoding).decode(bytes)
      }
    } catch (err) {
      console.log(' --- languageEncoding error : ', err)
    }
  }

  // mixed인 경우에는 text와 html을 동시에 가질 수 없으므로 text를 제거.
  if (mixedPart != null && textPart != null && htmlPart != null) {
    const boundary = mixedPart.typeInfo.boundary
    const textList = decodedString.split(boundary)
    const result = []
    textList.forEach((text: string) => {
      const dataList = text.split(/(\n\n)|(\r\n\r\n)/g)
      if (dataList.length < 2 || dataList[0].toLowerCase().includes('text/plain') === false) {
        result.push(text)
      }
    })
    const newString = result.join(boundary)
    postalMimeEML = await PostalMime.parse(newString)
  }

  if (postalMimeEML == null) {
    if (hasCharset === false) {
      postalMimeEML = await PostalMime.parse(decodedString)
    } else {
      postalMimeEML = await PostalMime.parse(buffer)
    }
  }

  return postalMimeEML
}

export interface EmailBodyPartInterface {
  hasHTMLBody(): boolean
  getHTMLBody(): string
  hasTextBody(): boolean
  getTextBody(): string
  isCalendar(): boolean
  getCalendarBody(): string
  files: any[]
}

export abstract class EmailObj implements EmailData {
  data: any
  reactMgr: any
  dataMgr: any
  isNew: boolean
  mailState: any
  extData: any
  isLoaded: boolean
  // 이메일이 draft인 경우, update될 때, 기존 draft가 삭제되고, 새로운 draft가 생성되므로, 새로운 draft의 id를 여기에 전달하여, draft로 인해 삭제되는 것을 알림.
  newEmailId?: string | null

  constructor(dataMgr: any) {
    this.reactMgr = creactReactObjMgr()
    this.dataMgr = dataMgr
    this.isNew = true
    this.mailState = EMAIL_STATE.NORMAL
    this.data = {}
    this.extData = {}
    this.isLoaded = null
    this.newEmailId = undefined
  }

  getExtData = (): any => {
    return this.extData
  }

  getId = () => {
    return this.data.id
  }

  setMailState = (state: any, value: boolean) => {
    if (value === true) {
      this.mailState |= state
    } else {
      this.mailState &= ~state
    }
    this.reactMgr.updateUI()
  }

  getMailState = () => {
    return this.mailState
  }

  resetMailState = () => {
    this.mailState = EMAIL_STATE.NORMAL
    this.reactMgr.updateUI()
  }

  isMailState = (state: any) => {
    if (state === EMAIL_STATE.NORMAL) {
      return (this.mailState & EMAIL_STATE.DELETE_MASK) === EMAIL_STATE.NORMAL
    }
    return (this.mailState & state) === state
  }

  getQuotedHeaderStr = (headerOptions: any) => {
    const { fromStr, receiveDate, toStr, ccTag, subject } = headerOptions
    const head = `
    <div id="quoted_header" style="clear:both;font-family: Calibri, Arial, Helvetica, sans-serif; font-size: 11pt;color:#000;" dir="auto">
      <hr style="border:none; height:1px; color:#E1E1E1; background-color:#E1E1E1; margin: 1em 0;">
      <div style="border:none; padding:3.0pt 0cm 0cm 0cm;" contenteditable="true" class="nine-pg" dir="auto">
        <div class="nine-pg" dir="auto">
          <span style="font-size:11.0pt;">
            <b>${i18n.t('email.field.from')}:</b> ${fromStr}<br>
            <b>${i18n.t('email.field.sendDate')}:</b> ${receiveDate}<br>
            <b>${i18n.t('email.field.to')}:</b> ${toStr}<br>${ccTag}
            <b>${i18n.t('email.field.subject')}:</b> ${subject}<br>
          </span>
        </div>
        <div class="nine-pg" dir="auto">
          <br>
        </div>
      </div>
    </div>`

    return head
  }

  parseEML = async (buffer: any): Promise<EmailBodyPartInterface> => {
    const postalMimeEML = await parseEMLByPostalMime(buffer)
    return this.makeBodyPartFromEML(postalMimeEML)
  }

  makeBodyPartFromEML = async (postalMimeEML: any): Promise<EmailBodyPartInterface> => {
    const { html, text, attachments } = postalMimeEML
    const bodyPart: any = {}
    bodyPart.hasHTMLBody = () => {
      return html != null
    }
    bodyPart.getHTMLBody = () => {
      return html || ''
    }
    bodyPart.hasTextBody = () => {
      return text != null
    }
    bodyPart.getTextBody = () => {
      return text || ''
    }
    bodyPart.isCalendar = () => {
      return false
    }
    bodyPart.getCalendarBody = () => {
      return ''
    }
    if (attachments != null && attachments.length > 0) {
      bodyPart.files = attachments
    }
    return Promise.resolve(bodyPart)
  }

  isMovable = () => {
    if (this.isDraft() === true) {
      return false
    }
    return true
  }

  abstract getViewSubject(): string
  abstract getViewTime(): string
  abstract isFlagEnabled(): boolean
  abstract hasAttachment(): boolean
  abstract getPreview(): string
  // 삭제 시 동장 모드
  // 'delete' - trash mailbox로 이동.
  // 'destroy' - 메일 완전 삭제.
  abstract getDeleteType(): string
  abstract isContained(mailbox: any): boolean
  abstract isDraft(): boolean
  abstract setRead(value: any, targetMailbox: any): any
  abstract isRead(): boolean
  abstract isFlagged(): boolean
  abstract reqDetail(reload?: boolean): Promise<any>
  abstract loadSelf(useNoti?: boolean): Promise<any>
  abstract generateQHeadStr(): Promise<any>
  abstract downloadEml(): Promise<any>
  abstract buildEML(attachmentList: any[]): Promise<any>
  abstract saveEML(attachmentList: any[]): Promise<any>
  abstract getPrimaryMessageId(): string | null
}

export default EmailObj
